import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { useAssortmentPlans } from '@/hooks/useAssortmentPlans'
import { useDepartments, useClasses, useSubclasses } from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { cn } from '@/utils/cn'
import {
  AGGREGATE_OPTIONS,
  buildAttributesRows,
  formatCurrency0,
  formatCurrency2,
  formatInteger,
  formatPercent,
  type AggregateBy,
  type AttributesFilterState,
} from './attributesMockData'

const ALL = '__all__'

const AP_STATUSES = [
  'Draft',
  'In Progress',
  'Submitted',
  'Approved',
  'Rejected',
  'Sent Back',
]

const BRANDS = [
  'Studio Active',
  'CORE',
  'Non-Private Brand',
  'Northbound',
  'Atelier',
  'Coastline',
  'EVERWEAR',
  'EmberRun',
  'Field & Pine',
  'Heritage Co.',
  'Loft Studio',
  'Ashbury',
  'Sunday Best',
]

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

interface AttributesReportMockupProps {
  lockedPlanId?: string
  lockedPlanName?: string
}

export function AttributesReportMockup({
  lockedPlanId,
  lockedPlanName,
}: AttributesReportMockupProps = {}) {
  const [aggregateBy, setAggregateBy] = useState<AggregateBy>('brand_name')
  const [planId, setPlanId] = useState<string | null>(lockedPlanId ?? null)
  const [seasonCalendarId, setSeasonCalendarId] = useState<string | null>(null)
  const [apStatus, setApStatus] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [brand, setBrand] = useState<string | null>(null)

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: plansResponse, isLoading: plansLoading } = useAssortmentPlans({
    pageSize: 100,
  })
  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()

  const filters = useMemo<AttributesFilterState>(
    () => ({
      planId,
      seasonCalendarId,
      apStatus,
      departmentId,
      classId,
      subclassId,
      brand,
    }),
    [
      planId,
      seasonCalendarId,
      apStatus,
      departmentId,
      classId,
      subclassId,
      brand,
    ]
  )

  const { rows, total } = useMemo(
    () => buildAttributesRows(aggregateBy, filters),
    [aggregateBy, filters]
  )

  const plans = plansResponse?.plans ?? []
  const lockedPlanLabel =
    lockedPlanId != null
      ? (plans.find(p => p.id === lockedPlanId)?.name ?? lockedPlanName ?? '')
      : ''
  const firstColLabel =
    AGGREGATE_OPTIONS.find(o => o.value === aggregateBy)?.label ?? 'Group'

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <span className="text-xs italic text-destructive">
            Assortment Reports
          </span>
          <span className="text-[11px] text-muted-foreground">
            Latest Last Update (TZ UTC-5)
          </span>
          <span className="text-[11px] text-muted-foreground">
            {lastUpdate}
          </span>
        </div>
        <div className="text-right text-[11px] text-muted-foreground">
          <div>Last Update (TZ UTC-5)</div>
          <div>{lastUpdate}</div>
        </div>
      </div>

      {/* Filter groups */}
      <div className="grid grid-cols-[140px_1fr_1fr_1fr_1fr] items-end gap-3">
        <span className="pb-2 text-sm font-semibold text-foreground">
          Assortment Plan Filters
        </span>
        <FilterField label="Assortment Plan">
          {lockedPlanId != null ? (
            <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-foreground">
              {lockedPlanLabel || (plansLoading ? 'Loading...' : '—')}
            </div>
          ) : (
            <Select
              value={planId ?? ALL}
              onValueChange={v => setPlanId(v === ALL ? null : v)}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue
                  placeholder={plansLoading ? 'Loading...' : 'All'}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {plans.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FilterField>
        <FilterField label="Season Calendar">
          <Select
            value={seasonCalendarId ?? ALL}
            onValueChange={v => setSeasonCalendarId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={seasonsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {seasons?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="AP Status">
          <Select
            value={apStatus ?? ALL}
            onValueChange={v => setApStatus(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Draft" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {AP_STATUSES.map(s => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <div />

        <span className="pb-2 text-sm font-semibold text-foreground">
          Item Filters
        </span>
        <FilterField label="Department">
          <Select
            value={departmentId ?? ALL}
            onValueChange={v => {
              setDepartmentId(v === ALL ? null : v)
              setClassId(null)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={departmentsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {departments?.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Class">
          <Select
            value={classId ?? ALL}
            disabled={!departmentId}
            onValueChange={v => {
              setClassId(v === ALL ? null : v)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={classesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {classes?.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Subclass">
          <Select
            value={subclassId ?? ALL}
            disabled={!classId}
            onValueChange={v => setSubclassId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={subclassesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {subclasses?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Brand">
          <Select
            value={brand ?? ALL}
            onValueChange={v => setBrand(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {BRANDS.map(b => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      </div>

      {/* Sidebar + Table */}
      <div className="flex gap-4">
        {/* Aggregate by sidebar */}
        <aside className="w-[180px] shrink-0 rounded-lg border border-border bg-white p-3 shadow-sm">
          <div className="mb-2 text-center text-sm font-semibold text-foreground">
            Aggregate by
          </div>
          <ScrollArea className="h-[420px] pr-2">
            <RadioGroup
              value={aggregateBy}
              onValueChange={v => setAggregateBy(v as AggregateBy)}
              className="gap-2"
            >
              {AGGREGATE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  htmlFor={`agg-${opt.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
                >
                  <RadioGroupItem
                    id={`agg-${opt.value}`}
                    value={opt.value}
                    className="cursor-pointer"
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </ScrollArea>
        </aside>

        {/* Main table */}
        <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-white shadow-sm">
          <ScrollArea className="w-full">
            <Table className="min-w-[2000px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="sticky left-0 z-10 w-[180px] bg-white text-xs font-semibold uppercase tracking-wide text-foreground">
                    {firstColLabel}
                  </TableHead>
                  <NumHead>Total Sales</NumHead>
                  <NumHead>Total Units</NumHead>
                  <NumHead>SKU Count</NumHead>
                  <NumHead>Reg/Promo Sales</NumHead>
                  <NumHead>Clearance Sales</NumHead>
                  <NumHead>AUR</NumHead>
                  <NumHead>AUC</NumHead>
                  <NumHead>Tariff Amount</NumHead>
                  <NumHead>Cost</NumHead>
                  <NumHead>GM</NumHead>
                  <NumHead>GM %</NumHead>
                  <NumHead>Discount %</NumHead>
                  <NumHead>Retail</NumHead>
                  <NumHead>Landed Cost</NumHead>
                  <NumHead>FOB Cost</NumHead>
                  <NumHead>Purchase Units</NumHead>
                  <NumHead>IMU%</NumHead>
                  <NumHead>Rec. AUR</NumHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell className="sticky left-0 z-10 bg-white font-medium text-foreground">
                      {row.label}
                    </TableCell>
                    <NumCell value={row.totalSales} fmt="cur0" />
                    <NumCell value={row.totalUnits} fmt="int" />
                    <NumCell value={row.skuCount} fmt="int" />
                    <NumCell value={row.regPromoSales} fmt="cur0" />
                    <NumCell value={row.clearanceSales} fmt="cur0" />
                    <NumCell value={row.aur} fmt="cur2" />
                    <NumCell value={row.auc} fmt="cur2" />
                    <NumCell value={row.tariffAmount} fmt="cur0" />
                    <NumCell value={row.cost} fmt="cur0" />
                    <NumCell value={row.gm} fmt="cur2" />
                    <NumCell value={row.gmPct} fmt="pct" />
                    <NumCell value={row.discountPct} fmt="pct" />
                    <NumCell value={row.retail} fmt="cur2" />
                    <NumCell value={row.landedCost} fmt="cur2" />
                    <NumCell value={row.fobCost} fmt="cur2" />
                    <NumCell value={row.purchaseUnits} fmt="int" />
                    <NumCell value={row.imuPct} fmt="pct" />
                    <NumCell value={row.recAur} fmt="cur2" />
                  </TableRow>
                ))}
                <TableRow className="border-t-2 bg-[#f5f7f6] font-semibold hover:bg-[#f5f7f6]">
                  <TableCell className="sticky left-0 z-10 bg-[#f5f7f6] font-semibold text-foreground">
                    {total.label}
                  </TableCell>
                  <NumCell value={total.totalSales} fmt="cur0" bold />
                  <NumCell value={total.totalUnits} fmt="int" bold />
                  <NumCell value={total.skuCount} fmt="int" bold />
                  <NumCell value={total.regPromoSales} fmt="cur0" bold />
                  <NumCell value={total.clearanceSales} fmt="cur0" bold />
                  <NumCell value={total.aur} fmt="cur2" bold />
                  <NumCell value={total.auc} fmt="cur2" bold />
                  <NumCell value={total.tariffAmount} fmt="cur0" bold />
                  <NumCell value={total.cost} fmt="cur0" bold />
                  <NumCell value={total.gm} fmt="cur2" bold />
                  <NumCell value={total.gmPct} fmt="pct" bold />
                  <NumCell value={total.discountPct} fmt="pct" bold />
                  <NumCell value={total.retail} fmt="cur2" bold />
                  <NumCell value={total.landedCost} fmt="cur2" bold />
                  <NumCell value={total.fobCost} fmt="cur2" bold />
                  <NumCell value={total.purchaseUnits} fmt="int" bold />
                  <NumCell value={total.imuPct} fmt="pct" bold />
                  <NumCell value={total.recAur} fmt="cur2" bold />
                </TableRow>
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}

function NumHead({ children }: { children: ReactNode }) {
  return (
    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
      {children}
    </TableHead>
  )
}

type NumFmt = 'cur0' | 'cur2' | 'int' | 'pct'

function NumCell({
  value,
  fmt,
  bold = false,
}: {
  value: number
  fmt: NumFmt
  bold?: boolean
}) {
  const text =
    fmt === 'cur0'
      ? formatCurrency0(value)
      : fmt === 'cur2'
        ? formatCurrency2(value)
        : fmt === 'int'
          ? formatInteger(value)
          : formatPercent(value)
  return (
    <TableCell
      className={cn(
        'text-right tabular-nums',
        value < 0 && 'text-destructive',
        bold && 'font-semibold'
      )}
    >
      {text}
    </TableCell>
  )
}
